import { Button, StandardDialog } from '@roc/ui';
import { observer } from 'mobx-react';
import { useGeneralTasksStore } from '../../hooks/useGeneralTasksStore';
import { useEffect, useMemo, useRef } from 'react';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import { Grid } from '@material-ui/core';
import { TaskPriority, TaskTypeForm } from '../../types/generalTasksTypes';
import { LoanTaskForm } from './components/loanTaskForm';
import { TechSupportForm } from './components/techSupportForm';
import { RadioGroup, RadioGroupOption } from './components/radioGroup';
import { capitalize, insertIf, isNil, LoanProcess } from '@roc/feature-utils';
import { NonLoanTaskForm } from './components/nonLoanTaskForm';
import { useUserStore } from '@roc/feature-app-core';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      width: '100%',
    },
    badge: {
      marginRight: '5px',
    },
  }),
);

type AddLoanTaskDialogProps = {
  open: boolean;
  loanId?: number;
  taskId?: number;
  handleClose: () => void;
  sectionName?: string;
  sectionId?: number;
  options: any[];
  loanProcess?: string;
  type2?: string;
  typeId2?: string;
  isSupportTask?: boolean;
  defaultPriority?: TaskPriority;
};

export const AddLoanTaskDialog = observer(({
  open,
  loanId,
  taskId,
  handleClose,
  sectionName,
  sectionId,
  options,
  loanProcess,
  type2,
  typeId2,
  isSupportTask = false,
  defaultPriority = TaskPriority.HIGH,
}: AddLoanTaskDialogProps) => {
  const { generalTasksStore } = useGeneralTasksStore();
  const { editTaskStore } = generalTasksStore;
  const { loanTaskFormStore, techTaskFormStore, nonLoanTaskFormStore } = editTaskStore;
  const classes = useStyles();
  const isSectionPreselected = !!(sectionName);
  const inputRef = useRef<HTMLInputElement>();
  const validatedLoanProcess = loanProcess || LoanProcess.UNDERWRITING;
  const { userStore } = useUserStore();

  const isInternalUser = useMemo(() => { return userStore.userInformation?.isInternal; }, [userStore.userInformation]);
  useEffect(() => {
    if (!open) return;

    const init = async () => {
      editTaskStore.reset();
      if (!isNil(loanId)) {
        await (editTaskStore.fetchLoanTaskSectionNames(loanId as number, capitalize(validatedLoanProcess)) as unknown as Promise<void>);
      }
      if (isSupportTask) {
        editTaskStore.onFieldChange('type', TaskTypeForm.TECH_SUPPORT);
        generalTasksStore.fetchSupportTaskWatcherOptions();
      } else if (!(generalTasksStore?.globalStore?.isInternalPortal || isInternalUser) && !isNil(loanId)) {
        generalTasksStore.fetchLoanActorsAssigneeOptions(loanId as number);
      } else {
        generalTasksStore.fetchUserInfoAndAssigneeOptions(loanId, null, taskId as number);
      }
      generalTasksStore.setLoanId(loanId as number);
      loanTaskFormStore.onFieldChange('loanId', loanId);
      techTaskFormStore.onFieldChange('loanId', loanId);
      nonLoanTaskFormStore.onFieldChange('loanId', loanId);
      loanTaskFormStore.onFieldChange('priority', defaultPriority);
      loanTaskFormStore.onFieldChange('type2', type2);
      loanTaskFormStore.onFieldChange('typeId2', typeId2);

      if (isSectionPreselected) {
        const matchingSection = editTaskStore.loanTaskSectionNames.find(s => s.category === sectionName);
        loanTaskFormStore.onFieldChange('sectionName', sectionName);
        loanTaskFormStore.onFieldChange('sectionId', matchingSection?.taskCategoryId ?? sectionId);
      }
      inputRef?.current?.blur();
    };

    init();
  }, [open]);

  const onClose = () => {
    handleClose();
    editTaskStore.reset();
  }

  const techSupportOption: RadioGroupOption = {
    label: TaskTypeForm.TECH_SUPPORT,
    value: TaskTypeForm.TECH_SUPPORT,
    onClick: () => {
      editTaskStore.onFieldChange('type', TaskTypeForm.TECH_SUPPORT);
      if (isSupportTask) {
        generalTasksStore.fetchSupportTaskWatcherOptions();
      }
    },
    checked: editTaskStore.form.fields.type.value === TaskTypeForm.TECH_SUPPORT,
  };

  const taskTypeRadioGroupOptions: RadioGroupOption[] = isSupportTask
    ? [techSupportOption]
    : [
        {
          label: TaskTypeForm.LOAN_TASK,
          value: TaskTypeForm.LOAN_TASK,
          onClick: () => {
            editTaskStore.onFieldChange('type', TaskTypeForm.LOAN_TASK);
          },
          checked:
            editTaskStore.form.fields.type.value === TaskTypeForm.LOAN_TASK,
        },
        techSupportOption,
        ...insertIf(
          generalTasksStore?.globalStore?.isInternalPortal ||
            (generalTasksStore?.globalStore?.isLoanChatApp && isInternalUser),
          [
            {
              label: TaskTypeForm.GENERAL_TASK,
              value: TaskTypeForm.GENERAL_TASK,
              onClick: () => {
                editTaskStore.onFieldChange('type', TaskTypeForm.GENERAL_TASK);
              },
              checked:
                editTaskStore.form.fields.type.value ===
                TaskTypeForm.GENERAL_TASK,
            },
          ]
        ),
      ];

  return (
    <>
      <StandardDialog
        open={open}
        title='Create Task'
        maxWidth='md'
        handleClose={() => { onClose() }}
        dialogContent={<Grid container spacing={2} innerRef={inputRef}>
          <Grid item xs={12} style={{ marginLeft: '-8px' }}>
            <RadioGroup
              label='Please select one: *'
              options={taskTypeRadioGroupOptions}
            />
          </Grid>
          {editTaskStore.form.fields.type.value === TaskTypeForm.LOAN_TASK && (
            <LoanTaskForm
              generalTasksStore={generalTasksStore}
              options={options}
              isSectionPreselected={isSectionPreselected}
            />
          )}
          {editTaskStore.form.fields.type.value === TaskTypeForm.TECH_SUPPORT && (
            <TechSupportForm generalTasksStore={generalTasksStore} />
          )}
          {editTaskStore.form.fields.type.value === TaskTypeForm.GENERAL_TASK && (
            <NonLoanTaskForm generalTasksStore={generalTasksStore} />
          )}
        </Grid>}
        dialogActions={
          <>
            <Button
              style={{ marginRight: '8px' }}
              onClick={() => { onClose() }}
              color="primary"
              variant="outlined"
              testId="cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                editTaskStore.addLoanTask(onClose);
              }}
              color="primary"
              variant="contained"
              testId="confirm"
            >
              Create
            </Button>
          </>
        }
      />
    </>

  );
});
